import { useMemo } from 'react';
import { APP_BASE_PATH } from 'shared/appBasePath';
import { buildNavigationUrl } from 'shared/routes';
import { LifecycleState } from 'api/GraphQL/queries/getVehicleSummaries';

type UseVehicleUrlsParams = {
  acquisitionName: string;
  appraisalId: string;
  isOfferAccepted: boolean;
  lifecycleState: LifecycleState;
};

type VehicleUrls = {
  url: string;
};

/**
 * Custom hook to construct the appropriate URL for a vehicle based on its lifecycle state.
 * Returns the correct URL for either viewing an offer/hub or resubmitting an expired offer.
 */
export const useVehicleUrls = ({
  acquisitionName,
  appraisalId,
  isOfferAccepted,
  lifecycleState
}: UseVehicleUrlsParams): VehicleUrls =>
  useMemo(() => {
    // For expired offers, determine the resubmit URL
    if (lifecycleState === LifecycleState.Expired) {
      return {
        url: buildNavigationUrl({
          baseUrl: `${APP_BASE_PATH}/refresh/appraisal/${appraisalId}`,
          includeReferrer: false
        })
      };
    }

    // For all other states, determine the offer or hub URL
    const baseUrlOfferOrHub =
      isOfferAccepted && lifecycleState.toLowerCase() !== 'rejected'
        ? `${APP_BASE_PATH}/hub/${acquisitionName}`
        : `${APP_BASE_PATH}/offer/${appraisalId}`;

    return {
      url: buildNavigationUrl({
        baseUrl: baseUrlOfferOrHub,
        includeReferrer: false
      })
    };
  }, [acquisitionName, appraisalId, isOfferAccepted, lifecycleState]);
